import React, { useState, useRef } from 'react';
import { triggerSlackSync, checkSyncStatus } from '../utils/slackSyncService';
import { format } from 'date-fns';

/**
 * Button component for triggering Slack data synchronization
 */
const SlackSyncButton = ({ organization, onSyncComplete }) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);
  const [error, setError] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [syncStatus, setSyncStatus] = useState('');
  const pollCountRef = useRef(0);
  const [syncSuccess, setSyncSuccess] = useState(false);
  
  // Check sync status on component mount
  React.useEffect(() => {
    const fetchSyncStatus = async () => {
      try {
        const status = await checkSyncStatus(organization);
        if (status.lastSynced) {
          setLastSynced(status.lastSynced);
        }
      } catch (err) {
        // Error checking sync status
      }
    };
    
    if (organization) {
      fetchSyncStatus();
    }
  }, [organization]);
  
  const handleSync = async () => {
    setIsSyncing(true);
    setError(null);
    setPollCount(0);
    setSyncStatus('Starting sync...');
    setSyncSuccess(false);
    pollCountRef.current = 0;
    
    try {
      console.log('Starting Slack sync...');
      const result = await triggerSlackSync(organization);
      
      if (result.success) {
        console.log('Sync initiated successfully, starting polling');
        setSyncStatus('Fetching Slack data (this may take several minutes)...');
        
        // If the sync completed immediately (using mock data), we can finish right away
        if (result.syncComplete) {
          console.log('Sync completed immediately');
          const status = await checkSyncStatus(organization);
          setLastSynced(status.lastSynced || new Date());
          setIsSyncing(false);
          setSyncStatus('Sync completed successfully!');
          setSyncSuccess(true);
          
          if (onSyncComplete) {
            onSyncComplete(status.lastSynced || new Date());
          }
          return;
        }
        
        // Store initial report count to detect changes
        const initialStatus = await checkSyncStatus(organization);
        const initialReportCount = initialStatus.reportCount || 0;
        console.log('Initial report count:', initialReportCount);
        console.log('Initial sync status:', initialStatus.syncStatus);
        
        // Poll for sync completion
        const pollInterval = setInterval(async () => {
          try {
            const status = await checkSyncStatus(organization);
            
            // Increment poll count
            pollCountRef.current += 1;
            setPollCount(pollCountRef.current);
            
            // Update status message based on poll count
            if (pollCountRef.current <= 2) {
              setSyncStatus('Fetching Slack data...');
            } else if (pollCountRef.current <= 4) {
              setSyncStatus('Processing messages...');
            } else if (pollCountRef.current <= 6) {
              setSyncStatus('Analyzing data...');
            } else if (pollCountRef.current <= 8) {
              setSyncStatus('Generating insights...');
            } else {
              setSyncStatus('Finalizing analysis...');
            }
            
            console.log(`Polling attempt ${pollCountRef.current}, report count: ${status.reportCount}, hasReports: ${status.hasReports}, syncStatus: ${status.syncStatus}`);
            
            // Consider sync complete if:
            // 1. We have a lastSynced timestamp that's recent, OR
            // 2. The report count has increased from our initial check, OR
            // 3. hasReports has changed from false to true, OR
            // 4. syncStatus is 'idle' (not 'syncing')
            if (
              (status.lastSynced && new Date(status.lastSynced) > new Date(Date.now() - 60000)) ||
              (status.reportCount > initialReportCount) ||
              (initialReportCount === 0 && status.hasReports) ||
              (status.syncStatus === 'idle' && initialStatus.syncStatus !== 'idle')
            ) {
              console.log('Sync completion detected!');
              clearInterval(pollInterval);
              setLastSynced(status.lastSynced || new Date());
              setIsSyncing(false);
              setSyncStatus('Sync completed successfully!');
              setSyncSuccess(true);
              
              if (onSyncComplete) {
                onSyncComplete(status.lastSynced || new Date());
              }
              return;
            }
            
            // After 12 polling attempts (60 seconds), force completion
            // This matches the backend timeout of 60 seconds
            if (pollCountRef.current >= 12) {
              console.log('Polling timeout reached, forcing completion');
              clearInterval(pollInterval);
              
              // Make one final check to see if any reports were created
              const finalStatus = await checkSyncStatus(organization);
              
              if (finalStatus.hasReports) {
                console.log('Reports found on final check, sync considered successful');
                setLastSynced(finalStatus.lastSynced || new Date());
                setIsSyncing(false);
                setSyncStatus('Sync completed successfully!');
                setSyncSuccess(true);
                
                if (onSyncComplete) {
                  onSyncComplete(finalStatus.lastSynced || new Date());
                }
              } else {
                console.log('No reports found on final check, showing error');
                setError('Sync timed out. Please try again.');
                setIsSyncing(false);
                setSyncStatus('Sync failed.');
              }
              return;
            }
            
            // If there's an error in the sync status, show it and stop polling
            if (status.lastError) {
              console.error('Sync error detected:', status.lastError);
              setError(`Sync error: ${status.lastError}`);
              clearInterval(pollInterval);
              setIsSyncing(false);
              setSyncStatus('Sync failed.');
              return;
            }
          } catch (err) {
            console.error('Error during polling:', err);
          }
        }, 5000); // Poll every 5 seconds
        
        // Stop polling after 5 minutes (prevent infinite polling)
        setTimeout(() => {
          if (isSyncing) {
            console.log('Final timeout reached, forcing completion');
            clearInterval(pollInterval);
            setIsSyncing(false);
            setError('Sync timed out after 5 minutes. The process might still be running on the server.');
            setSyncStatus('Sync timed out.');
          }
        }, 300000); // 5 minutes
      } else {
        setError('Sync failed to start');
        setIsSyncing(false);
        setSyncStatus('Sync failed.');
      }
    } catch (err) {
      console.error('Error during sync:', err);
      setError(err.message || 'Error syncing Slack data');
      setIsSyncing(false);
      setSyncStatus('Sync failed.');
    }
  };

  return (
    <div className="flex items-center">
      <button
        onClick={handleSync}
        disabled={isSyncing}
        className={`p-1.5 rounded-md transition-colors ${
          isSyncing ? 'text-grey-60 cursor-not-allowed' : 'text-grey-60 hover:text-black-0 hover:bg-grey-20'
        }`}
        title={isSyncing ? `Syncing... ${pollCount > 0 ? `(${pollCount})` : ''}` : 'Sync Slack Data'}
      >
        {isSyncing ? (
          <svg className="animate-spin w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
        )}
      </button>
    </div>
  );
};

export default SlackSyncButton; 